import { defineStore } from 'pinia'

export interface SlideField {
  id: string
  type: 'title' | 'subtitle' | 'content' | 'bulletPoints' | 'image' | 'metrics' | 'testimonials' | 'team' | 'advisors' | 'charts'
  value: string | string[] | any
  editable: boolean
}

export interface Slide {
  id: string
  name: string
  component: string
  fields: SlideField[]
  slideData?: any // For complex slide-specific data
  duration?: number // Duration in seconds for auto-advance
  showGrid?: boolean // Whether to show background grid for this slide
}

export interface ColorPalette {
  id: string
  name: string
  primary: string
  secondary: string
  accent: string
  success: string
  warning: string
  error: string
  muted: string
}

export const useSlidesStore = defineStore('slides', {
  state: () => ({
    currentSlideIndex: 0,
    isEditMode: false,
    showBackground: false,
    showBeforeAfterOverlay: false,
    currentColorPalette: 'greyscale' as string,
    // Auto-advance functionality
    isAutoAdvanceEnabled: false,
    currentSlideStartTime: null as number | null,
    autoAdvanceTimer: null as NodeJS.Timeout | null,
    defaultSlideDuration: 10, // Default 10 seconds per slide
    colorPalettes: [
      {
        id: 'professional',
        name: 'Professional Blue',
        primary: 'rgb(37, 99, 235)', // blue-600
        secondary: 'rgb(59, 130, 246)', // blue-500
        accent: 'rgb(147, 197, 253)', // blue-300
        success: 'rgb(34, 197, 94)', // green-500
        warning: 'rgb(245, 158, 11)', // amber-500
        error: 'rgb(239, 68, 68)', // red-500
        muted: 'rgb(100, 116, 139)' // slate-500
      },
      {
        id: 'modern',
        name: 'Modern Purple',
        primary: 'rgb(124, 58, 237)', // violet-600
        secondary: 'rgb(139, 92, 246)', // violet-500
        accent: 'rgb(196, 181, 253)', // violet-300
        success: 'rgb(34, 197, 94)', // green-500
        warning: 'rgb(245, 158, 11)', // amber-500
        error: 'rgb(239, 68, 68)', // red-500
        muted: 'rgb(100, 116, 139)' // slate-500
      },
      {
        id: 'tech',
        name: 'Tech Cyan',
        primary: 'rgb(8, 145, 178)', // cyan-600
        secondary: 'rgb(34, 211, 238)', // cyan-400
        accent: 'rgb(165, 243, 252)', // cyan-200
        success: 'rgb(34, 197, 94)', // green-500
        warning: 'rgb(245, 158, 11)', // amber-500
        error: 'rgb(239, 68, 68)', // red-500
        muted: 'rgb(100, 116, 139)' // slate-500
      },
      {
        id: 'warm',
        name: 'Warm Orange',
        primary: 'rgb(234, 88, 12)', // orange-600
        secondary: 'rgb(251, 146, 60)', // orange-400
        accent: 'rgb(254, 215, 170)', // orange-200
        success: 'rgb(34, 197, 94)', // green-500
        warning: 'rgb(245, 158, 11)', // amber-500
        error: 'rgb(239, 68, 68)', // red-500
        muted: 'rgb(100, 116, 139)' // slate-500
      },
      {
        id: 'greyscale',
        name: 'Pure Greyscale',
        primary: 'rgb(38, 38, 38)', // true grey-800
        secondary: 'rgb(64, 64, 64)', // true grey-700
        accent: 'rgb(115, 115, 115)', // true grey-500
        success: 'rgb(38, 38, 38)', // true grey-800
        warning: 'rgb(140, 140, 140)', // true grey-400
        error: 'rgb(82, 82, 82)', // true grey-600
        muted: 'rgb(163, 163, 163)' // true grey-400
      },
      {
        id: 'monochrome',
        name: 'Black & White',
        primary: 'rgb(0, 0, 0)', // pure black
        secondary: 'rgb(51, 51, 51)', // dark grey
        accent: 'rgb(128, 128, 128)', // medium grey
        success: 'rgb(0, 0, 0)', // pure black
        warning: 'rgb(102, 102, 102)', // light grey
        error: 'rgb(77, 77, 77)', // darker grey
        muted: 'rgb(179, 179, 179)' // lighter grey
      },
      {
        id: 'gradient',
        name: 'Gradient Spectrum',
        primary: 'rgb(59, 130, 246)', // blue-500
        secondary: 'rgb(139, 92, 246)', // violet-500
        accent: 'rgb(236, 72, 153)', // pink-500
        success: 'rgb(34, 197, 94)', // green-500
        warning: 'rgb(251, 146, 60)', // orange-400
        error: 'rgb(239, 68, 68)', // red-500
        muted: 'rgb(156, 163, 175)' // gray-400
      }
    ] as ColorPalette[],
    slides: [
      {
        id: 'title',
        name: 'Title Slide',
        component: 'TitleSlide',
        duration: 10,
        fields: [
          {
            id: 'headline',
            type: 'title',
            value: 'Build Agents That Build Agents ',
            editable: true
          },
          {
            id: 'company-name',
            type: 'subtitle',
            value: 'BRX.ai',
            editable: true
          }
        ],
        slideData: {
          visuals: {
            logo: "Colorful gradient square, large white letters 'BRX' centered",
            background: "Pure white"
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "01/13"
          }
        }
      },
      {
        id: 'problem',
        name: 'Problem Statement',
        component: 'ProblemSlide',
        fields: [
          {
            id: 'problem-title',
            type: 'title',
            value: 'The Problem: The AI Agent Development Bottleneck',
            editable: true
          },
          {
            id: 'problem-intro',
            type: 'subtitle',
            value: 'Current development is often a painful choice:',
            editable: true
          },
          {
            id: 'problem-points',
            type: 'bulletPoints',
            value: [
              'No-Code/Low-Code Tools: Too simplistic for complex, custom agent logic; hit a power ceiling quickly for technical teams.',
              'Code-Heavy Frameworks (e.g., LangChain): Powerful but notoriously complex, slow to iterate with, require deep specialized expertise, but most importantly, there is a lack of accuracy and precision in LLMs.',
              'Result: Wasted developer time, slow innovation, high costs.'
            ],
            editable: true
          }
        ],
        slideData: {
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "02/13"
          }
        }
      },
      {
        id: 'solution',
        name: 'Our Solution',
        component: 'SolutionSlide',
        fields: [
          {
            id: 'solution-title',
            type: 'title',
            value: 'Solution: Visual Speed, Developer Power',
            editable: true
          },
          {
            id: 'solution-content',
            type: 'content',
            value: 'We give developers a visual framework to build complex AI agents radically faster without sacrificing control.',
            editable: true
          },
          {
            id: 'core-idea',
            type: 'subtitle',
            value: 'Core Idea',
            editable: true
          },
          {
            id: 'core-idea-text',
            type: 'content',
            value: 'On BRX, everything is composable. We allow you to design intricate agent logic (LLM calls, decision-making, data flows) on an intuitive canvas, then deploy with minimal friction.',
            editable: true
          },
          {
            id: 'tagline',
            type: 'content',
            value: 'It\'s the "Unreal Engine" for AI agents — abstracting complexity, unleashing creativity.',
            editable: true
          }
        ],
        slideData: {
          visuals: {
            screenshot: {
              description: "Dark-themed BRX.ai IDE with three panels: left 'Explorer' listing project files, top bar tabs ('Chat Bot X', 'Classify User Request', 'Generate JSON Schema', 'Generate Code', 'Generate Prompt'), center canvas showing node graph, right collapsed code panel",
              ui_top_tabs: [
                "Chat Bot X",
                "Classify User Request", 
                "Generate JSON Schema",
                "Generate Code",
                "Generate Prompt"
              ],
              sidebar_entries: [
                "Root Folder",
                "Imported BRX",
                "Classify User Request",
                "Generate Prompt",
                "Generate JSON Schema", 
                "Generate Code",
                "New Project"
              ]
            }
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "03/13"
          }
        }
      },
      {
        id: 'differentiator1',
        name: 'Differentiator 1',
        component: 'DifferentiatorSlide',
        fields: [
          {
            id: 'diff1-title',
            type: 'title',
            value: 'Build Complex Agents Visually, At Speed',
            editable: true
          },
          {
            id: 'what-it-is',
            type: 'subtitle',
            value: 'What it is',
            editable: true
          },
          {
            id: 'what-it-is-text',
            type: 'content',
            value: 'Developers visually map out agent behavior using smart, reusable modular functions and intelligent "switches" (semantic routers) that automatically guide tasks.',
            editable: true
          },
          {
            id: 'why-it-matters',
            type: 'subtitle',
            value: 'Why it matters',
            editable: true
          },
          {
            id: 'why-it-matters-points',
            type: 'bulletPoints',
            value: [
              'Drastically Faster Development: Slash agent build times from weeks/months to days.',
              'Reduced Complexity: Developers focus on what the agent does, not how to code every step.',
              'Easier Maintenance & Iteration: Clear visual logic means faster updates and less debugging.'
            ],
            editable: true
          }
        ],
        slideData: {
          visuals: {
            screenshot: {
              description: "Blank BRX.ai project canvas: centre prompt 'Double Click to create a new BRX', left panel showing 'Explorer' collapsed"
            }
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "04/13"
          }
        }
      },
      {
        id: 'differentiator2',
        name: 'Differentiator 2',
        component: 'DifferentiatorSlide',
        fields: [
          {
            id: 'diff2-title',
            type: 'title',
            value: 'Secure, "Live" Code Execution – Solved.',
            editable: true
          },
          {
            id: 'what-it-is-2',
            type: 'subtitle',
            value: 'What it is',
            editable: true
          },
          {
            id: 'what-it-is-text-2',
            type: 'content',
            value: 'Our platform uniquely allows AI agents to write and run custom code snippets during their operation, all within a secure sandbox.',
            editable: true
          },
          {
            id: 'why-it-matters-2',
            type: 'subtitle',
            value: 'Why it matters',
            editable: true
          },
          {
            id: 'why-it-matters-points-2',
            type: 'bulletPoints',
            value: [
              'Runtime Adaptability: Agents must dynamically rewrite and embed static code on the fly to meet evolving functional demands.',
              'Massive Security & Ops Lift: We handle the sandboxing headache, a major barrier and risk for others. BRX executes each agent in a disposable sandbox, eliminating persistent attack surfaces and containing risk.'
            ],
            editable: true
          }
        ],
        slideData: {
          visuals: {
            screenshot: {
              description: "Sandboxed file explorer in BRX.ai showing multiple test apps and code files (e.g. 'Test App', 'gssheetWriter')",
              file_list_sample: [
                "Test App",
                "gssheetWriter", 
                "simpleClone",
                "exeTests"
              ]
            }
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "05/13"
          }
        }
      },
      // {
      //   id: 'why-now',
      //   name: 'Why Now',
      //   component: 'WhyNowSlide',
      //   duration: 15,
      //   fields: [
      //     {
      //       id: 'why-now-title',
      //       type: 'title',
      //       value: 'Why Now',
      //       editable: true
      //     },
      //     {
      //       id: 'bullet-points',
      //       type: 'bulletPoints',
      //       value: [
      //         {
      //           title: 'Explosive LLM Adoption & Complexity',
      //           description: '10× surge in LLM API calls over the past 12 months. Enterprises running multi-step AI pipelines ⇒ Loss of control & spiraling costs'
      //         },
      //         {
      //           title: 'Lack of Fine-Grained Control',
      //           description: 'Current tools (LangChain/Crew) offer either "powerful but clunky" or "simple but limited". No turnkey way to compose, steer, and monitor task-level AI workflows'
      //         },
      //         {
      //           title: 'Soaring Compute & Developer Costs',
      //           description: 'LLM token pricing ↑ 35% YoY; developers spending 30% of time debugging prompts. Inefficiencies translate directly into higher burn and slower time-to-market'
      //         },
      //         {
      //           title: 'Market & Investor Tailwinds',
      //           description: '$15B developer tools market (CAGR = 18%). VCs are hunting "next-gen AI infrastructure" (30+ funds publicly increasing AI tools investments in 2025)'
      //         },
      //         {
      //           title: 'Our Moment',
      //           description: 'Proprietary, composable AI agent engine ready for alpha sites (Lockheed, Big Pharma). Backhoe-vs-shovel analogy: we\'re the heavy-duty solution for complex, enterprise-scale AI'
      //         }
      //       ],
      //       editable: true
      //     }
      //   ],
      //   slideData: {
      //     visuals: {
      //       chart: {
      //         type: 'growth_and_cost',
      //         description: 'LLM API usage curve + rising cost line showing exponential growth in usage and steady increase in costs'
      //       },
      //       analogy: {
      //         type: 'backhoe_vs_shovel',
      //         description: 'Visual comparison showing BRX as heavy-duty backhoe vs alternatives as simple tools'
      //       }
      //     },
      //     footer: {
      //       left: "BRX.ai",
      //       center: "BRX: Official Distribution DECK",
      //       right: "06/13"
      //     }
      //   }
      // },
      {
        id: 'why-now-industrial',
        name: 'Why Now - Industrial Revolution',
        component: 'WhyNowIndustrialSlide',
        duration: 20,
        fields: [
          {
            id: 'why-now-industrial-title',
            type: 'title',
            value: 'From pickaxes to excavators: the AI development industrial revolution',
            editable: true
          },
          {
            id: 'why-now-industrial-subtitle',
            type: 'subtitle',
            value: 'Industrial consistency for mission critical AI',
            editable: true
          },
          {
            id: 'pickaxe-points',
            type: 'bulletPoints',
            value: [
              'Unreliable AI agents with long development cycles',
              'Unpredictable, inconsistent outputs',
              'High error rates, limited control'
            ],
            editable: true
          },
          {
            id: 'brx-points',
            type: 'bulletPoints',
            value: [
              'Complete control over decision-making logic',
              'Surgical precision in agent behavior',
              'Revolutionary visual development interface'
            ],
            editable: true
          },
          {
            id: 'why-now-industrial-moment-title',
            type: 'title',
            value: 'Why this moment matters',
            editable: true
          },
          {
            id: 'moment-points',
            type: 'bulletPoints',
            value: [
              'Companies need speed + precision + reliability',
              'This is the infrastructure moment for AI agents'
            ],
            editable: true
          }
        ],
        slideData: {
          visuals: {
            comparison: {
              left: {
                type: 'potter_wheel',
                description: 'Potter wheel with wonky, different sized pots',
                success_rate: '40%'
              },
              right: {
                type: 'assembly_line',
                description: 'Factory assembly line with identical perfect products',
                success_rate: '99.9%'
              }
            },
            matrix: {
              type: 'harvey_balls_comparison',
              metrics: ['Speed', 'Precision', 'Consistency', 'Control', 'Accessibility'],
              pickaxe_values: ['25%', '25%', '25%', '25%', '25%'],
              brx_values: ['100%', '100%', '100%', '100%', '100%']
            }
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "06.5/13"
          }
        }
      },
      {
        id: 'vision',
        name: 'The Vision',
        component: 'VisionSlide',
        showGrid: false, // Clean title slide without grid
        fields: [
          {
            id: 'vision-headline',
            type: 'title',
            value: 'We Build Computers That Build Computers',
            editable: true
          }
        ],
        slideData: {
          visuals: {
            logo: "Same colorful BRX gradient square, centered"
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "07/13"
          }
        }
      },
      {
        id: 'market',
        name: 'Market Opportunity',
        component: 'MarketSlide',
        fields: [
          {
            id: 'market-title',
            type: 'title',
            value: 'Market: The AI agent era needs infrastructure',
            editable: true
          },
          {
            id: 'market-description',
            type: 'content',
            value: 'Every industry is racing to deploy AI agents for automation, customer experience, and new product lines. The current toolset is inadequate, creating a massive opportunity for a platform that truly empowers developers.',
            editable: true
          },
          {
            id: 'market-metrics',
            type: 'metrics',
            value: {
              TAM: "$90B",
              SAM: "$10.8B", 
              SOM: "$432M"
            },
            editable: true
          }
        ],
        slideData: {
          visuals: {
            graphic: "Three-layer concentric circles labeled TAM (outer), SAM (middle), SOM (inner), with respective values in blue"
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "08/13"
          }
        }
      },
      // {
      //   id: 'business-model',
      //   name: 'Business Model',
      //   component: 'BusinessModelSlide',
      //   fields: [
      //     {
      //       id: 'business-title',
      //       type: 'title',
      //       value: 'Business Model',
      //       editable: true
      //     },
      //     {
      //       id: 'business-points',
      //       type: 'bulletPoints',
      //       value: [
      //         'Subscription/Hosted Version',
      //         'Subscription for web app - $200/M + usage',
      //         'Hosted - $1000/month + usage',
      //         'Compute usage: $0.21/1K executions',
      //         'Licensing starting at $24,000/year + fees'
      //       ],
      //       editable: true
      //     }
      //   ],
      //   slideData: {
      //     path_to_100m_arr: {
      //       developers: {
      //         active_users: 200000,
      //         ARR: "$20M ARR"
      //       },
      //       SMB_mid_market: {
      //         customers: 500,
      //         ARR: "$80M ARR"
      //       },
      //       total_ARR_target: "$100M ARR"
      //     },
      //     visuals: {
      //       diagram: "PATH TO $100M ARR: 200,000 ACTIVE USERS = $20M ARR + 500 CUSTOMERS = $80M ARR"
      //     },
      //     footer: {
      //       left: "BRX.ai",
      //       center: "BRX: Official Distribution DECK",
      //       right: "09/13"
      //     }
      //   }
      // },
      {
        id: 'adoption',
        name: 'Market Adoption',
        component: 'AdoptionSlide',
        fields: [
          {
            id: 'adoption-title',
            type: 'title',
            value: 'Market Adoption',
            editable: true
          },
          {
            id: 'adoption-subtitle',
            type: 'subtitle',
            value: 'We are targeting 3 key verticals: Big Data, Biotech & Insurance',
            editable: true
          },
          {
            id: 'revenue-value',
            type: 'content',
            value: 'Our current traction',
            editable: true
          },
          {
            id: 'adoption-charts',
            type: 'charts',
            value: [
              {
                chart_type: "stacked area",
                series: [
                  { name: "new", color: "teal" },
                  { name: "returning", color: "magenta" }
                ],
                x_axis: "Date (Jan 1 2024 → Mar 8 2025)",
                y_axis: "Cumulative users (0 → 8 000)"
              },
              {
                chart_type: "line",
                series: [
                  { name: "total_token_usage", color: "blue" }
                ],
                x_axis: "Date (Jan 1 2024 → Dec 6 2024)",
                y_axis: "Cumulative tokens (0 → 125 M)"
              }
            ],
            editable: false
          }
        ],
        slideData: {
          key_metrics: {
            customers: 3,
            customer_pricing: "$200/M + usage",
            revenue_to_date: "$60K Rev",
            active_users: "5.7K",
            target_verticals: ["Big Data", "Biotech", "Insurance"]
          },
          visuals: {
            stat_panels: [
              { label: "3 Customers", value: "$200/M + usage", note: "" },
              { label: "$60K Rev", value: "", note: "" },
              { label: "5.7K", value: "Active users", note: "" }
            ]
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "10/13"
          }
        }
      },
      {
        id: 'social-proof',
        name: 'Social Proof',
        component: 'SocialProofSlide',
        fields: [
          {
            id: 'social-title',
            type: 'title',
            value: 'We See AI Differently',
            editable: true
          },
          {
            id: 'social-content',
            type: 'content',
            value: 'We have been heads down focusing on what development teams truly need in order to build/deploy agentic systems, precision. Agents have been our vision since 2021 & we have been building the infrastructure to make it possible since.',
            editable: true
          },
          {
            id: 'testimonials',
            type: 'testimonials',
            value: [
              {
                author: "Robert Scoble",
                handle: "@Scobleizer",
                date: "Aug 11, 2024",
                text_snippet: "A new AI language arrives: … He is starting a new company, BRX …",
                views: "31.9K"
              },
              {
                author: "Jando",
                handle: "@Jandodev",
                date: "Sep 23, 2021",
                text_snippet: "… I finally got into the GPT-3 beta after applying a billion times this is a whole new form of coding it's going to change everything"
              },
              {
                author: "Robert Scoble",
                handle: "@Scobleizer",
                date: "Jul 25, 2024",
                text_snippet: "@Jandodev just showed me a prompt humans can't read but LLMs understand this language better …"
              },
              {
                author: "Elon Musk",
                handle: "@elonmusk",
                date: "Jul 26, 2024",
                text_snippet: "Intelligence seems to be about how tightly you can compress reality and, from that compression, predict the future."
              }
            ],
            editable: true
          }
        ],
        slideData: {
          visuals: {
            photos: [
              "Crowd at BRX.ai event, people clustered around laptops",
              "Group shot of ~30 attendees smiling at camera"
            ]
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "11/13"
          }
        }
      },
      {
        id: 'team',
        name: 'Our Team',
        component: 'TeamSlide',
        fields: [
          {
            id: 'team-title',
            type: 'title',
            value: 'Team',
            editable: true
          },
          {
            id: 'team-members',
            type: 'team',
            value: [
              {
                name: "Jake Anderson",
                role: "CEO",
                image: "Headshot of Jake Anderson wearing light grey hoodie against dark wall",
                bio_bullets: [
                  "Full-stack developer with Fortune 500 project experience",
                  "Over 15 000 subs & 10 000 000+ views on developer/engineer YouTube",
                  "Head of Development at Dubai startup",
                  "Senior Kernel Developer at IBM (3rd generation IBM engineer)"
                ]
              },
              {
                name: "Daniel Fritz",
                role: "COO",
                image: "Headshot of Daniel Fritz smiling, black shirt, dark backdrop",
                bio_bullets: [
                  "VP Sales at Rock Ledge Group",
                  "7 years of entrepreneurial experience in the CPG supply chain space",
                  "1 exit as founder",
                  "Built a team of 10 sales professionals delivering over $120 million in customer contracts in the healthcare supply chain market"
                ]
              }
            ],
            editable: true
          }
        ],
        slideData: {
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "12/13"
          }
        }
      },
      {
        id: 'advisors',
        name: 'Advisors',
        component: 'AdvisorsSlide',
        fields: [
          {
            id: 'advisors-title',
            type: 'title',
            value: 'Advisors',
            editable: true
          },
          {
            id: 'advisors-list',
            type: 'advisors',
            value: [
              {
                name: "Andy Jenks",
                image: "Photo of Andy Jenks in office with glass windows",
                bullets: [
                  "Widely recognized leader in cloud-based electronic discovery",
                  "Over $13 billion in deep tech/software exits"
                ]
              },
              {
                name: "Howard Berke",
                image: "Professional headshot of Howard Berke in suit & tie",
                bullets: [
                  "Delegate to the World Economic Forum Technology Pioneers",
                  "40 years of VC/technology startup experience"
                ]
              },
              {
                name: "Michael Enescu",
                image: "On-stage photo of Michael Enescu presenting at conference",
                bullets: [
                  "Former CTO, Cisco",
                  "Founder, Linux Foundation",
                  "Chief Architect, SAP"
                ]
              },
              {
                name: "Igor Polovinchik",
                image: "Headshot of Igor Polovinchik with bookshelf background",
                bullets: [
                  "Startup advisor for technical companies",
                  "CIO/SVP at ZoomInfo"
                ]
              },
              {
                name: "Gabriel Paunescu",
                image: "Photo of Gabriel Paunescu mid-presentation, gesturing with right hand",
                bullets: [
                  "CEO, NaoLogic",
                  "Three-time founder"
                ]
              }
            ],
            editable: true
          }
        ],
        slideData: {
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "13/14"
          }
        }
      },
      {
        id: 'closing',
        name: 'Closing',
        component: 'ClosingSlide',
        showGrid: false, // Clean closing slide without grid
        fields: [
          {
            id: 'closing-headline',
            type: 'title',
            value: 'Amplify Not Replace',
            editable: true
          }
        ],
        slideData: {
          visuals: {
            logo: "Colorful gradient BRX square, centered"
          },
          footer: {
            left: "BRX.ai",
            center: "BRX: Official Distribution DECK",
            right: "14/14"
          }
        }
      }
    ] as Slide[]
  }),

  getters: {
    currentSlide: (state) => state.slides[state.currentSlideIndex],
    totalSlides: (state) => state.slides.length,
    canGoNext: (state) => state.currentSlideIndex < state.slides.length - 1,
    canGoPrevious: (state) => state.currentSlideIndex > 0,
    
    // Get current slide's grid setting (defaults to true if not specified)
    showGrid: (state) => {
      const currentSlide = state.slides[state.currentSlideIndex]
      return currentSlide?.showGrid !== false // Default to true unless explicitly set to false
    },
    
    // Get all editable fields for AI operations
    getAllEditableFields: (state) => {
      const fields: SlideField[] = []
      state.slides.forEach(slide => {
        slide.fields.forEach(field => {
          if (field.editable) {
            fields.push(field)
          }
        })
      })
      return fields
    },

    // Get fields by type for targeted AI operations
    getFieldsByType: (state) => (type: SlideField['type']) => {
      const fields: SlideField[] = []
      state.slides.forEach(slide => {
        slide.fields.forEach(field => {
          if (field.type === type && field.editable) {
            fields.push(field)
          }
        })
      })
      return fields
    }
  },

  actions: {
    // Navigation actions
    nextSlide() {
      if (this.canGoNext) {
        this.currentSlideIndex++
        // Restart timer if auto-advance is enabled
        if (this.isAutoAdvanceEnabled) {
          this.startSlideTimer()
        }
      }
    },

    previousSlide() {
      if (this.canGoPrevious) {
        this.currentSlideIndex--
        // Restart timer if auto-advance is enabled
        if (this.isAutoAdvanceEnabled) {
          this.startSlideTimer()
        }
      }
    },

    goToSlide(index: number) {
      if (index >= 0 && index < this.slides.length) {
        this.currentSlideIndex = index
        // Restart timer if auto-advance is enabled
        if (this.isAutoAdvanceEnabled) {
          this.startSlideTimer()
        }
      }
    },

    // Edit mode actions
    toggleEditMode() {
      this.isEditMode = !this.isEditMode
    },

    setEditMode(enabled: boolean) {
      this.isEditMode = enabled
    },

    // Background toggle actions
    toggleBackground() {
      this.showBackground = !this.showBackground
    },

    setBackground(enabled: boolean) {
      this.showBackground = enabled
    },

    // Before/After overlay toggle actions
    toggleBeforeAfterOverlay() {
      this.showBeforeAfterOverlay = !this.showBeforeAfterOverlay
    },

    setBeforeAfterOverlay(enabled: boolean) {
      this.showBeforeAfterOverlay = enabled
    },

    // Field editing actions
    updateField(slideId: string, fieldId: string, value: string | string[]) {
      const slide = this.slides.find(s => s.id === slideId)
      if (slide) {
        const field = slide.fields.find(f => f.id === fieldId)
        if (field && field.editable) {
          field.value = value
        }
      }
    },

    // Bulk update for AI operations
    updateMultipleFields(updates: Array<{ slideId: string, fieldId: string, value: string | string[] }>) {
      updates.forEach(update => {
        this.updateField(update.slideId, update.fieldId, update.value)
      })
    },

    // Get field value helper
    getFieldValue(slideId: string, fieldId: string): string | string[] | undefined {
      const slide = this.slides.find(s => s.id === slideId)
      if (slide) {
        const field = slide.fields.find(f => f.id === fieldId)
        return field?.value
      }
      return undefined
    },

    // Color palette actions
    setColorPalette(paletteId: string) {
      if (this.colorPalettes.find(p => p.id === paletteId)) {
        this.currentColorPalette = paletteId
        this.applyColorPalette()
      }
    },

    getCurrentPalette(): ColorPalette | undefined {
      return this.colorPalettes.find(p => p.id === this.currentColorPalette)
    },

    applyColorPalette() {
      const palette = this.getCurrentPalette()
      if (palette && typeof document !== 'undefined') {
        const root = document.documentElement
        root.style.setProperty('--slide-primary', palette.primary)
        root.style.setProperty('--slide-secondary', palette.secondary)
        root.style.setProperty('--slide-accent', palette.accent)
        root.style.setProperty('--slide-success', palette.success)
        root.style.setProperty('--slide-warning', palette.warning)
        root.style.setProperty('--slide-error', palette.error)
        root.style.setProperty('--slide-muted', palette.muted)
      }
    },

    // Auto-advance functionality
    toggleAutoAdvance() {
      this.isAutoAdvanceEnabled = !this.isAutoAdvanceEnabled
      if (this.isAutoAdvanceEnabled) {
        this.startSlideTimer()
      } else {
        this.stopSlideTimer()
      }
    },

    setAutoAdvance(enabled: boolean) {
      this.isAutoAdvanceEnabled = enabled
      if (enabled) {
        this.startSlideTimer()
      } else {
        this.stopSlideTimer()
      }
    },

    startSlideTimer() {
      this.stopSlideTimer() // Clear any existing timer
      this.currentSlideStartTime = Date.now()
      
      const currentSlide = this.currentSlide
      const duration = (currentSlide?.duration || this.defaultSlideDuration) * 1000 // Convert to milliseconds
      
      this.autoAdvanceTimer = setTimeout(() => {
        if (this.isAutoAdvanceEnabled && this.canGoNext) {
          this.nextSlide()
        } else if (this.isAutoAdvanceEnabled && !this.canGoNext) {
          // Reached the end, stop auto-advance
          this.setAutoAdvance(false)
        }
      }, duration)
    },

    stopSlideTimer() {
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer)
        this.autoAdvanceTimer = null
      }
      this.currentSlideStartTime = null
    },

    // Get remaining time for current slide
    getRemainingTime(): number {
      if (!this.isAutoAdvanceEnabled || !this.currentSlideStartTime) {
        return 0
      }
      
      const currentSlide = this.currentSlide
      const duration = (currentSlide?.duration || this.defaultSlideDuration) * 1000
      const elapsed = Date.now() - this.currentSlideStartTime
      const remaining = Math.max(0, duration - elapsed)
      
      return Math.ceil(remaining / 1000) // Return in seconds
    },

    // Get progress percentage for current slide
    getSlideProgress(): number {
      if (!this.isAutoAdvanceEnabled || !this.currentSlideStartTime) {
        return 0
      }
      
      const currentSlide = this.currentSlide
      const duration = (currentSlide?.duration || this.defaultSlideDuration) * 1000
      const elapsed = Date.now() - this.currentSlideStartTime
      const progress = Math.min(100, (elapsed / duration) * 100)
      
      return progress
    },

    // Update slide duration
    updateSlideDuration(slideId: string, duration: number) {
      const slide = this.slides.find(s => s.id === slideId)
      if (slide) {
        slide.duration = duration
        // If this is the current slide and auto-advance is enabled, restart timer
        if (slide === this.currentSlide && this.isAutoAdvanceEnabled) {
          this.startSlideTimer()
        }
      }
    },

    // Set default duration for all slides
    setDefaultDuration(duration: number) {
      this.defaultSlideDuration = duration
      // If auto-advance is enabled and current slide has no custom duration, restart timer
      if (this.isAutoAdvanceEnabled && !this.currentSlide?.duration) {
        this.startSlideTimer()
      }
    },

    // Grid control actions
    updateSlideGrid(slideId: string, showGrid: boolean) {
      const slide = this.slides.find(s => s.id === slideId)
      if (slide) {
        slide.showGrid = showGrid
      }
    },

    toggleCurrentSlideGrid() {
      const currentSlide = this.currentSlide
      if (currentSlide) {
        currentSlide.showGrid = !this.showGrid
      }
    },

    // Keyboard navigation
    handleKeyNavigation(event: KeyboardEvent) {
      switch (event.key) {
        case 'ArrowRight':
        case ' ':
          event.preventDefault()
          this.nextSlide()
          break
        case 'ArrowLeft':
          event.preventDefault()
          this.previousSlide()
          break
        case 'Home':
          event.preventDefault()
          this.goToSlide(0)
          break
        case 'End':
          event.preventDefault()
          this.goToSlide(this.slides.length - 1)
          break
        case 'Escape':
          event.preventDefault()
          this.setEditMode(false)
          break
      }
    }
  }
})
